import { FormikValues } from 'formik';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import * as Yup from 'yup';

import { Box, Button, Card, Typography } from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateTraining } from '../Training.hooks';
import { Training, BaseTrainingCardProps } from '../Training.interface';

const frequencyOptions = _.range(1, 25);

const TrainingRecurringCard: React.FC<BaseTrainingCardProps> = (
  props: BaseTrainingCardProps
) => {
  const { training, onChange, onUpdate, onError } = props;
  const formikRef = useRef<FormikValues>();
  const {
    training: updatedTraining,
    isLoading,
    error,
    patch
  } = useUpdateTraining(training.id);
  const { isFieldDisabled } = useTaskEntityFields(training.task);
  const isFrequencyDisabled: boolean = isFieldDisabled('frequency');
  const hasFrequency: boolean =
    !!training.frequency || typeof training.frequency === 'number';

  function handleSubmit(dto: Partial<Training>) {
    patch(dto);
    onChange?.(dto);
  }

  function handleShowRecurring() {
    handleSubmit({ frequency: 1 });
  }

  function handleCancelRecurring() {
    handleSubmit({ frequency: null });
  }

  useEffect(() => {
    if (updatedTraining) {
      onChange?.(updatedTraining);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTraining]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card>
      <CardTitle>
        <ScheduleIcon />
        Recurring Schedule
      </CardTitle>
      <CardContent padding='small'>
        <Box mb={2}>
          <Typography display='block' variant='caption'>
            Setup recurring training to automate reminders to employees keep
            them compliant with company policies.
          </Typography>
        </Box>

        {!hasFrequency && (
          <Button
            variant='outlined'
            color='primary'
            disabled={isFrequencyDisabled}
            onClick={() => handleShowRecurring()}
          >
            Setup Recurring Training
          </Button>
        )}

        {hasFrequency && (
          <InlineFormik
            strict={false}
            innerRef={(formik: any) => (formikRef.current = formik)}
            initialValues={{ frequency: parseInt(`${training.frequency}`) }}
            validationSchema={Yup.object({
              frequency: Yup.number().required('Required')
            })}
            error={error}
            isSubmitting={isLoading}
            onSubmit={(dto: Partial<Training>) => {
              handleSubmit({
                ...dto,
                frequency: parseInt(`${dto.frequency}`) || null
              });
            }}
          >
            <InlineForm>
              <InlineSelect
                name='frequency'
                label=''
                disabled={isFrequencyDisabled}
                options={frequencyOptions?.map(n => ({
                  value: n.toString(),
                  label: n === 1 ? `Monthly` : `Every ${n} months`
                }))}
              />

              <Box mt={1} ml={1}>
                <Button
                  variant='contained'
                  disabled={isFrequencyDisabled}
                  onClick={() => handleCancelRecurring()}
                >
                  Cancel Recurring Training
                </Button>
              </Box>
            </InlineForm>
          </InlineFormik>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingRecurringCard;
